import { bunja } from 'bunja';
import { v4 as uuid } from 'uuid';

import {
  postOneWayConditionalOrderApi,
  postOneWayOrderApi,
  postOrderApi,
  postPositionApi,
  putAdjustLeverageApi,
} from '@flipster/apis/v2/trade';
import { leverageRestrictionBunja } from '@flipster/features/src/margin-bonus/leverage-restriction.state';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { orderAttributionAtom } from '@flipster/features/src/shared/order-attribution';
import { tradingSessionCheckBunja } from '@flipster/features/src/shared/trading-suspension/state';
import { i18nBunja } from '@flipster/i18n';
import { refDetailTimes } from '@flipster/r230522/original/server-time';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { dateToEpochString } from '@flipster/utils/datetime';
import { getErrorMessage } from '@flipster/utils/error';
import { getFlipsterConfigsV2Key, getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';
import Num, { BigNumber, numberFormatter } from '@flipster/utils/num';

import { perpetualSymbolSocketBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import { yourPositionsBunja } from '../../../etc-section/perpetual/your-postions/state';
import { OrderEventContext } from '../../tracker';
import { getSlLiquidationError } from '../advanced-tpsl';
import {
  isValidTriggerReferencePrice,
  marginTypeBunja,
  oneWayPositionLeverageBunja,
  perpAccountBunja,
  symbolBunja,
  symbolOccupiedPositionBunja,
} from './base';
import { bracketBunja } from './bracket';
import { eventBunja } from './event';
import { formBunja } from './form';
import { liquidationPreviewBunja } from './liquidation';
import { buildOrderRequest, type OrderRequestPlan } from './logic/action/order-request';
import { classifySubmitError } from './logic/action/submit-error';
import { realtimeStateBunja } from './price';
import { validationBunja } from './validation';

/**
 * submitOrder가 UI에 열도록 요청하는 다이얼로그 신호.
 * 다이얼로그는 JSX/컴포넌트라 데이터 레이어가 직접 렌더하지 않고 UI로 위임한다(`name`별로 UI가 분기).
 * 토스트는 명령형 서비스(snackbar)라 bunja가 직접 호출한다. 새 다이얼로그는 이 union에 추가.
 */
export type SubmitDialog = { name: 'orderNotPlaced'; maxInitLeverageRestriction: number | undefined };

/**
 * submitOrder 반환값. `openDialog`가 있으면 UI가 해당 다이얼로그를 연다.
 * undefined면 다이얼로그 없음(성공·검증 거부·토스트만 있는 실패 — 토스트/인라인은 내부에서 처리 완료).
 */
export type SubmitOrderResult = { openDialog: SubmitDialog };

export const submitBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { getT } = bunja.use(i18nBunja);
  const form = bunja.use(formBunja);
  const { symbol, symbolInfoAtom } = bunja.use(symbolBunja);
  const { getDetailAtom } = bunja.use(perpetualSymbolSocketBunja);
  const { tradeModeAtom } = bunja.use(perpAccountBunja);
  const { leverageRestrictionAtom } = bunja.use(leverageRestrictionBunja);
  const { quantityAtom } = bunja.use(realtimeStateBunja);
  const { onOrderSuccess, onOrderFailed } = bunja.use(eventBunja);
  const { symbolOccupiedPositionAtom } = bunja.use(symbolOccupiedPositionBunja);
  const { marginTypeAtom } = bunja.use(marginTypeBunja);
  const { userAtom } = bunja.use(authBunja);
  const { payloadAtom } = bunja.use(validationBunja);
  const { positionTabTypeAtom } = bunja.use(yourPositionsBunja);
  const { bracketValidationAtom } = bunja.use(bracketBunja);
  const { hasOneWayPositionAtom, positionLeverageAtom } = bunja.use(oneWayPositionLeverageBunja);
  const { checkTradable, guardOffHoursAlert } = bunja.use(tradingSessionCheckBunja);
  const { liquidationPriceAtom } = bunja.use(liquidationPreviewBunja);

  const transTrade = getT('Trade.r250401');
  const t = getT();

  /**
   * 주문 타입별 제출 결과. true면 공통 성공 후처리(캐시 persist·reset·onOrderSuccess) 진행,
   * false면 제출 전 검증에서 거부됨(후처리 스킵). API 에러는 예외로 던져져 submitOrder catch가 처리.
   */
  type SubmitOutcome = Promise<boolean>;

  /** Market 주문 제출: API 호출 + IOC 부분체결/완전체결 토스트 + 포지션 탭 전환. */
  const submitMarketOrder = async (
    ctx: OrderEventContext,
    plan: OrderRequestPlan,
    reduceOnly: boolean,
  ): SubmitOutcome => {
    const symbolInfo = store.get(symbolInfoAtom);
    const isIoc = (order: { remainingQty?: string }) =>
      !!order.remainingQty && Num(order.remainingQty).isGreaterThan(0);
    // IOC 부분체결 토스트(MP/OW 공통). Reduce-only는 서버 응답에 의존하므로 호출 측에서 게이팅한다.
    const showPartialFillToast = (order: { size?: string; remainingQty?: string; price?: string }) => {
      const filled = BigNumber.max(
        Num(order.size || '0')
          .abs()
          .minus(Num(order.remainingQty || '0').abs()),
        0,
      );
      const remaining = Num(order.remainingQty || '0').abs();
      snackbar.error({
        message: t('Trade.r260224.Market_order_partial_fill_toast', {
          amountSymbolFilled: `${numberFormatter(filled, { precision: symbolInfo?.tradeQtyPrecision, toFixed: true })} ${symbolInfo?.positionUnit}`,
          value: numberFormatter(order.price || '0', { precision: symbolInfo?.pricePrecision, toFixed: true }),
          amountSymbolRemaining: `${numberFormatter(remaining, { precision: symbolInfo?.tradeQtyPrecision, toFixed: true })} ${symbolInfo?.positionUnit}`,
        }),
      });
    };

    switch (plan.variant) {
      case 'marketMulti': {
        ctx.mark('apiStartTime');
        const { data } = await postPositionApi(symbol, plan.body);
        ctx.markRequestId(data.position.positionId);
        ctx.mark('apiEndTime');
        if (isIoc(data.order)) {
          if (!reduceOnly) showPartialFillToast(data.order);
        } else {
          snackbar.success({ message: transTrade('Order_executed_toast', { symbol: symbolInfo?.displayShortName }) });
        }
        break;
      }
      case 'marketOneway': {
        ctx.mark('apiStartTime');
        ctx.socketResponseSupported = false;
        const { data } = await postOneWayOrderApi(symbol, plan.body);
        ctx.mark('apiEndTime');
        // OneWay market 완전체결은 소켓 구동이라 토스트 없음(부분체결만 안내).
        if (isIoc(data.order) && !reduceOnly) showPartialFillToast(data.order);
        break;
      }
      default:
        throw new Error(`submitMarketOrder: unexpected variant ${plan.variant}`);
    }
    store.set(positionTabTypeAtom, 'open');
    return true;
  };

  /** Limit 주문 제출: API 호출 + submitted 토스트. */
  const submitLimitOrder = async (ctx: OrderEventContext, plan: OrderRequestPlan): SubmitOutcome => {
    const symbolInfo = store.get(symbolInfoAtom);
    switch (plan.variant) {
      case 'limitMulti': {
        ctx.mark('apiStartTime');
        const { data } = await postPositionApi(symbol, plan.body);
        ctx.markRequestId(data.position.positionId);
        ctx.mark('apiEndTime');
        break;
      }
      case 'limitOneway': {
        ctx.mark('apiStartTime');
        ctx.socketResponseSupported = false;
        await postOneWayOrderApi(symbol, plan.body);
        ctx.mark('apiEndTime');
        break;
      }
      default:
        throw new Error(`submitLimitOrder: unexpected variant ${plan.variant}`);
    }
    snackbar.success({ message: transTrade('Order_submitted_toast', { symbol: symbolInfo?.displayShortName }) });
    return true;
  };

  /**
   * Trigger(Price) 주문 제출: 기준가 사전 검증 → API 호출 + submitted 토스트. (Trigger Time은 sunset — ERD-216 §11)
   * 기준가가 유효하지 않으면 에러 토스트 후 false 반환(제출 안 함).
   */
  const submitTriggerOrder = async (
    ctx: OrderEventContext,
    plan: OrderRequestPlan,
    triggerReferencePrice: string,
  ): SubmitOutcome => {
    if (!isValidTriggerReferencePrice(triggerReferencePrice)) {
      snackbar.error({ message: t('Common.r250401.Unexpected_error_message_multiline') });
      return false;
    }
    const symbolInfo = store.get(symbolInfoAtom);
    switch (plan.variant) {
      case 'triggerMulti': {
        ctx.mark('apiStartTime');
        const { data } = await postOrderApi(symbol, plan.body);
        const order = data.orders.find((order) => 'orderId' in order);
        if (order) {
          ctx.markRequestId(order.orderId);
        } else {
          ctx.socketResponseSupported = false;
        }
        ctx.mark('apiEndTime');
        break;
      }
      case 'triggerOneway': {
        ctx.mark('apiStartTime');
        ctx.socketResponseSupported = false;
        await postOneWayConditionalOrderApi(symbol, plan.body);
        ctx.mark('apiEndTime');
        break;
      }
      default:
        throw new Error(`submitTriggerOrder: unexpected variant ${plan.variant}`);
    }
    snackbar.success({ message: transTrade('Order_submitted_toast', { symbol: symbolInfo?.displayShortName }) });
    return true;
  };

  const submitOrder = async (ctx: OrderEventContext): Promise<SubmitOrderResult | undefined> => {
    const symbolInfo = store.get(symbolInfoAtom);
    const { maxInitLeverageRestriction } = store.get(leverageRestrictionAtom);
    const tradeMode = store.get(tradeModeAtom);
    const attribution = store.get(orderAttributionAtom);
    const marginType = store.get(marginTypeAtom);

    const {
      side,
      orderState,
      amount,
      referencePrice,
      triggerPrice = '0',
      triggerPriceType,
      triggerReferencePrice,
      reduceOnly,
      tpSl,
      advancedTpSl,
      priceType,
      triggerExecutionType,
      limitPrice = '0',
      isBbo,
    } = store.get(payloadAtom);

    const { kind, leverage } = orderState;
    if (side === 'unknown' || !tradeMode) return;

    if (!checkTradable()) return;
    // Tradable off-hours: 신규 포지션 방향(Long/Short, non reduce-only) 주문 전 off-hours 안내 (ERD-240 §8).
    // reduce-only는 exempt. 사용자가 다이얼로그를 닫으면(Got It 아님) 주문 취소.
    if (!reduceOnly && !(await guardOffHoursAlert('openOrder'))) return;
    try {
      store.set(form.orderTypeFormFieldErrorAtom, null);
      const requestId = uuid();
      const timestamp = dateToEpochString(new Date());

      // SL은 예상 청산가를 침범할 수 없다. 클릭 시점에 확정된 side로 post-fill 청산가 프리뷰와 비교한다.
      // (side가 미정인 채로는 어느 청산가와 비교할지 알 수 없어 폼 입력 중에는 검사하지 않고 제출 시 검사)
      if (tpSl.enabled && tpSl.sl) {
        const { long, short } = store.get(liquidationPriceAtom);
        const slLiquidationError = getSlLiquidationError({
          side,
          slPrice: tpSl.sl,
          liqPrice: side === 'Long' ? long : short,
        });
        if (slLiquidationError) {
          snackbar.error({
            message: transTrade(slLiquidationError.messageKey, {
              liquidationPrice: numberFormatter(slLiquidationError.liquidationPrice),
            }),
          });
          return;
        }
      }

      const refTimestamp = refDetailTimes[symbol];

      /**
       * issue thread: https://prextech.slack.com/archives/C01JGTJHR8E/p1730867195834949
       * 관련 공식: https://www.notion.so/prextech/Formula-in-Order-Form-12aeb32ad22780b38edadd82d1fd1363?pvs=4#12aeb32ad2278002ba6cd2c7ddd5904d
       * 공식 참고: https://prextech.slack.com/archives/C04407SABK4/p1731397573232789
       *
       * Reduce-only는 기존 포지션을 줄이는 주문이므로 notional max로 막지 않고 서버 검증에 위임한다.
       */
      if (!reduceOnly) {
        const buffer = 0.01;
        const occupiedPositionSize = store.get(symbolOccupiedPositionAtom)[symbol]?.[side] ?? '0';
        const margin = Num(amount ?? '0').dividedBy(leverage);
        const condition1 = Num(symbolInfo?.notionalMaxOrderAmount ?? '0').dividedBy(leverage);
        const temp = Num(symbolInfo?.notionalMaxPosition ?? '0').minus(
          Num(occupiedPositionSize).multipliedBy(1 + buffer),
        );
        const condition2 = temp.dividedBy(leverage);

        // 이번 주문 1건이 per-order 최대치를 넘는 경우 → 주문 수량 축소 유도
        if (margin.isGreaterThan(condition1)) {
          snackbar.error({ message: transTrade('Exceed_max_order_quantity_error') });
          return;
        }
        // 기존 포지션 + 이번 주문이 notional 포지션 한도(cap)에 도달한 경우 → 기존 포지션 정리 유도
        if (margin.isGreaterThan(condition2)) {
          snackbar.error({ message: transTrade('Size_limit_reached_error') });
          return;
        }
      }

      const hasOneWayPosition = store.get(hasOneWayPositionAtom);
      const positionLeverage = store.get(positionLeverageAtom);
      if (hasOneWayPosition && positionLeverage != null && leverage > positionLeverage) {
        try {
          await putAdjustLeverageApi(symbol, 0, {
            requestId: uuid(),
            timestamp: dateToEpochString(new Date()),
            leverage,
            attribution,
          });
        } catch (error) {
          snackbar.error({ message: getErrorMessage(error) });
          return;
        }
      }

      store.set(form.shouldShowBracketErrorAtom, false);
      const { isValid: isBracketValid } = store.get(bracketValidationAtom);
      if (!isBracketValid) {
        store.set(form.shouldShowBracketErrorAtom, true);
        return;
      }

      // 서버 요청 본문 조립은 순수 빌더에 위임한다. 여기(submitOrder)는 검증 가드·API 호출·토스트만 담당.
      const pswapDetail = store.get(getDetailAtom);
      const plan = buildOrderRequest({
        side,
        priceType,
        triggerExecutionType,
        triggerPrice,
        triggerPriceType,
        triggerReferencePrice,
        kind,
        leverage,
        amount,
        qty: store.get(quantityAtom),
        reduceOnly,
        referencePrice,
        limitPrice,
        isBbo,
        tpSl,
        advancedTpSl,
        tradeMode,
        hasOneWayPosition,
        marginType,
        attribution,
        askPrice: pswapDetail?.askPrice,
        bidPrice: pswapDetail?.bidPrice,
        slippage: {
          enabled: store.get(form.slippageEnabledAtom),
          mode: store.get(form.slippageModeAtom),
          value: store.get(form.slippageValueAtom),
        },
        requestId,
        timestamp,
        refTimestamp,
      });
      // 주문 타입별 제출 위임(각 함수가 사전 검증·API 호출·마크·토스트·탭 전환을 담당).
      // 반환값이 false면 제출 전 검증 거부 → 공통 성공 후처리를 건너뛴다.
      let submitted: boolean;
      switch (priceType) {
        case 'market':
          submitted = await submitMarketOrder(ctx, plan, reduceOnly);
          break;
        case 'limit':
          submitted = await submitLimitOrder(ctx, plan);
          break;
        case 'trigger':
          submitted = await submitTriggerOrder(ctx, plan, triggerReferencePrice);
          break;
        default:
          priceType satisfies never;
          throw new Error(`Unexpected priceType: ${priceType}`);
      }
      if (!submitted) return;

      // 주문 성공 시 제출한 leverage/marginType을 per-symbol v2 캐시에 기록한다. (PRD §4 트리거 ③)
      // cold-start 값(Isolated, 1x)이어도 사용자의 명시적 선택으로 보고 기록한다.
      const userId = store.get(userAtom)?.userId;
      if (userId != null) {
        const leverageKey = getFlipsterConfigsV2Key(userId, 'leverage');
        setLocalStorage(leverageKey, { ...getLocalStorage(leverageKey), [symbol]: leverage });
        const marginTypeKey = getFlipsterConfigsV2Key(userId, 'marginType');
        setLocalStorage(marginTypeKey, { ...getLocalStorage(marginTypeKey), [symbol]: marginType });
      }

      // Max slippage is a per-order, ephemeral control — reset after a successful submit (ERD-233).
      form.resetSlippage();
      // 제출 성공 시 설정해 둔 Advanced TP/SL preset을 초기화한다(다음 주문에 이월 방지).
      // 실패(catch) 시에는 재시도를 위해 유지하므로 성공 경로에서만 clear한다.
      store.set(form.clearAdvancedTpSlAtom);

      onOrderSuccess(ctx);
    } catch (error) {
      ctx.mark('apiEndTime');
      // Reset the per-order slippage control after a failed submit too (ERD-233).
      form.resetSlippage();
      const errorAction = classifySubmitError(error, kind);
      let message: string;
      switch (errorAction.type) {
        case 'autoLeverageRestricted':
          // 다이얼로그(JSX)만 UI가 연다. 여기선 열 다이얼로그 + max를 신호로 반환.
          return { openDialog: { name: 'orderNotPlaced', maxInitLeverageRestriction } };
        case 'slippageExceeded':
          // ERD-233: 허용 슬리피지 내 0-fill → localized copy 우선.
          message = t('Trade.r260611.Order_was_canceled_because_the_market_price_exceeded_your_max_slippage_setting');
          break;
        case 'notEnoughBalance':
          // 서버 메시지 + 해당 입력 필드에 인라인 에러 표시(인라인은 form 상태라 여기 유지).
          message = getErrorMessage(error);
          store.set(form.orderTypeFormFieldErrorAtom, { field: errorAction.field, message });
          break;
        case 'unknown':
          message = getErrorMessage(error);
          break;
      }
      // 토스트는 명령형 서비스라 여기서 직접 표시(다이얼로그와 달리 UI 위임 불필요). 다이얼로그 없음 → undefined 반환.
      snackbar.error({ message });
      onOrderFailed(ctx, { type: 'api', error: message });
    }
  };

  return {
    payloadAtom,
    submitOrder,
  };
});
submitBunja.debugLabel = 'submitBunja';
